/**
 * BottomNav — Tab bar with icons and labels
 *
 * Used for: main app navigation (Koti, Haku, Myy, Suosikit, Profiili)
 */
import type { ReactNode } from 'react'
import { Platform } from 'react-native'
import { styled, XStack, YStack, Text as TamaguiText, type GetProps } from 'tamagui'

const BottomNavFrame = styled(XStack, {
  name: 'BottomNav',
  bg: '$backgroundCard',
  borderTopLeftRadius: '$xl',
  borderTopRightRadius: '$xl',
  pt: 10,
  pb: Platform.OS === 'web' ? 16 : 20,
  px: '$sm',
  jc: 'space-around',
  ai: 'center',
  shadowColor: '$shadowColor',
  shadowOffset: { width: 0, height: -4 },
  shadowOpacity: 0.08,
  shadowRadius: 12,
})

const NavItem = styled(YStack, {
  name: 'NavItem',
  ai: 'center',
  gap: 2,
  px: '$sm',
  py: '$xs',
  br: '$md',
  cursor: 'pointer',
  pressStyle: { opacity: 0.7 },

  variants: {
    active: {
      true: {
        bg: 'rgba(116, 140, 112, 0.15)',
      },
    },
  } as const,
})

const NavLabel = styled(TamaguiText, {
  name: 'NavLabel',
  fontFamily: '$body',
  fontSize: 11,
  fontWeight: '500',

  variants: {
    active: {
      true: { color: '$colorPrimary', fontWeight: '600' },
      false: { color: '$colorSecondary' },
    },
  } as const,

  defaultVariants: {
    active: false,
  },
})

type BottomNavFrameProps = GetProps<typeof BottomNavFrame>

interface NavTab {
  key: string
  label: string
  icon: (props: { active: boolean }) => ReactNode
}

interface BottomNavProps extends BottomNavFrameProps {
  tabs: NavTab[]
  activeKey: string
  onTabPress?: (key: string) => void
}

export const BottomNav = BottomNavFrame.styleable<BottomNavProps>(
  ({ tabs, activeKey, onTabPress, ...props }, ref) => {
    return (
      <BottomNavFrame ref={ref} {...props}>
        {tabs.map((tab) => {
          const isActive = tab.key === activeKey
          return (
            <NavItem
              key={tab.key}
              active={isActive}
              onPress={() => onTabPress?.(tab.key)}
            >
              {tab.icon({ active: isActive })}
              <NavLabel active={isActive}>{tab.label}</NavLabel>
            </NavItem>
          )
        })}
      </BottomNavFrame>
    )
  },
)
